// SightingList - the ONE "seen at" renderer for a unique file's sightings
// (content identity layer 1, design 3.7 / R2 / R8d). Reused by the Files tab
// provenance column, the sync trees, the quarantine / failed-extraction /
// wrong-site panels AND the conflict modal's per-candidate source list, so every
// surface agrees (frontends "surfaces that must agree" rule).
//
// The component owns its own COLLAPSE (INRT-2560 / docs/design-sighting-collapse-
// and-dedup-rendering.md): a file seen in more than one place renders as a single
// clickable "Seen in N places" summary, so no caller can flood a panel with N x 5
// provenance lines. A file with ONE sighting renders that line inline by default;
// ``collapseSingle`` gives it a "Seen in 1 place" counter instead, which the Files
// tab's Source column uses so the whole column reads as a tidy list of counters.
import { useState } from "react";
import type { Sighting } from "../api/hooks/useUploadsList";
import styles from "./SightingList.module.css";
import { Chevron } from "./Chevron";

// Date + HH:MM (UTC) - mirrors the agent renderer's fmt_datetime so the operator
// and the model see the same minute-precision timestamps and can order arrivals
// within a day. The stored timestamps are UTC; toISOString keeps them in UTC.
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 16).replace("T", " ");
}

// Date-only (UTC), for the email-thread span line.
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// The real per-source arrival/modification time each sighting displays (Drive
// modification, email received, manual first-seen) - mirrors the BE
// sighting_sort_key so this list reads in the same chronological order the agent
// sees, not last_seen_at sync bookkeeping. Missing -> 0 (sorts last).
function sortMs(s: Sighting): number {
  const iso =
    s.source === "drive_sync"
      ? s.drive_modified_time || s.last_seen_at || s.first_seen_at
      : s.source === "gmail_attachment" ||
          s.source === "gmail_zip_member" ||
          s.source === "gmail_thread"
        ? s.received_at || s.first_seen_at || s.last_seen_at
        : s.first_seen_at || s.last_seen_at;
  const ms = iso ? new Date(iso).getTime() : 0;
  return Number.isNaN(ms) ? 0 : ms;
}

// Mirrors the BE sighting_render._address_list: every address on the thread, so
// the operator and the model see the same distribution list.
function addressList(values: string[] | undefined, label: string): string {
  const cleaned = (values ?? []).filter((v) => v && v.trim());
  if (cleaned.length === 0) return "";
  return ` (${label}: ${cleaned.join(", ")})`;
}

// "(5 messages, 2026-01-05 to 2026-01-08)" - how big the conversation is and how
// long it ran. Mirrors the BE sighting_render._thread_extent; each part is omitted
// when absent, so a sighting written before the span was recorded still renders
// its arrival date.
function threadExtent(s: Sighting): string {
  const parts: string[] = [];
  const count = s.thread_message_count;
  if (typeof count === "number" && count > 0) {
    parts.push(`${count} message${count === 1 ? "" : "s"}`);
  }
  const first = fmtDate(s.thread_first_message_at);
  const last = fmtDate(s.received_at);
  if (first && last) {
    parts.push(first === last ? first : `${first} to ${last}`);
  } else if (last) {
    parts.push(`received ${last}`);
  }
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

// "(3 attachments: a.pdf, b.pdf, and 1 more)" - mirrors the BE
// sighting_render._thread_attachments. Every stored name renders; the "and N
// more" tail covers a count that exceeds the names recorded against it - a
// legacy row written under the old per-thread name cap, or an attachment whose
// filename was unusable.
function threadAttachments(s: Sighting): string {
  const blob = s.thread_attachments;
  const count = blob?.count;
  if (typeof count !== "number" || count <= 0) return "";
  const names = (blob?.names ?? []).filter((n) => n && n.trim());
  const noun = count === 1 ? "attachment" : "attachments";
  if (names.length === 0) return ` (${count} ${noun})`;
  const more = count - names.length;
  return ` (${count} ${noun}: ${names.join(", ")}${more > 0 ? `, and ${more} more` : ""})`;
}

// The thread's message count, read off its gmail_thread sighting - the one place
// a thread's shape lives now that it is provenance rather than an extracted fact.
export function threadMessageCount(sightings: Sighting[] | undefined): number | null {
  // Newest first, not array order: a thread whose subject changed can carry an
  // older gmail_thread sighting alongside the current one, and the current one is
  // the count to show.
  const threads = (sightings ?? [])
    .filter((s) => s.source === "gmail_thread")
    .sort((a, b) => sortMs(b) - sortMs(a));
  for (const s of threads) {
    if (typeof s.thread_message_count === "number") return s.thread_message_count;
  }
  return null;
}

// Mirrors the agent-context renderer (sighting_render.render_sighting) so the Files
// tab / conflict modal / tracker and the model see identical "seen at" text. The
// CHANNEL label is driven by the upload's canonical `origin` (metadata_blob.source),
// because sighting.source is unreliable (a gmail/drive upload can land a 'manual'
// sighting via the completion door); `source` is only used for detail.
function describe(s: Sighting): string {
  const src = s.channel;

  const by = s.uploaded_by ? ` by ${s.uploaded_by}` : "";
  const on = s.first_seen_at ? ` on ${fmtDateTime(s.first_seen_at)}` : "";

  if (src === "drive_sync") {
    const flag = s.deprecated ? "DEPRECATED PATH" : "current";
    // The file's IN-DRIVE modification time (document currency), not last_seen_at
    // (our sync bookkeeping, which for a bulk sync is just the run time).
    const when = s.drive_modified_time
      ? `, modified ${fmtDateTime(s.drive_modified_time)}`
      : s.last_seen_at
        ? `, last synced ${fmtDateTime(s.last_seen_at)}`
        : "";
    return `Drive sync: "${s.drive_path || s.display_name}" (${flag}${when})`;
  }
  if (src === "drive_sync_zip_member") {
    const loc = s.drive_path || s.source_zip_filename || "a Drive archive";
    const when = s.drive_modified_time ? `, modified ${fmtDateTime(s.drive_modified_time)}` : "";
    return `Drive sync: member of archive "${loc}"${when}`;
  }
  if (src === "drive_pick") {
    return `Drive picker: "${s.display_name}"${by}${on}`;
  }
  if (src === "gmail_attachment" || src === "gmail_zip_member") {
    const thread = s.thread_subject ? ` on thread "${s.thread_subject}"` : "";
    const from = s.sender ? ` from ${s.sender}` : "";
    const to = addressList(s.recipients?.to, "to");
    const cc = addressList(s.recipients?.cc, "cc");
    // Attachments keep minute precision (one message); thread bodies use date-only.
    const recv = s.received_at ? `, received ${fmtDateTime(s.received_at)}` : "";
    const zip = s.source_zip_filename ? ` (from archive ${s.source_zip_filename})` : "";
    return `Gmail sync: email attachment "${s.display_name}"${thread}${from}${to}${cc}${recv}${zip}`;
  }
  if (src === "gmail_thread") {
    // picker (a /new Gmail pick, origin 'gmail') vs automated sync ('gmail_sync').
    const channel = origin === "gmail" ? "Gmail picker" : "Gmail sync";
    // Recover the subject from display_name ("<subject> [<threadId>].json") when
    // thread_subject is NULL (historical rows).
    const subjectText =
      s.thread_subject || s.display_name.replace(/\s*\[[^\]]*\]\.json$/i, "").trim();
    const subj = subjectText ? ` "${subjectText}"` : "";
    const to = addressList(s.recipients?.to, "to");
    const cc = addressList(s.recipients?.cc, "cc");
    // Attachments before recipients: the agent-side prefix cuts a long line from
    // the RIGHT, so the more load-bearing part goes first. Mirrors the BE.
    return `${channel}: email thread${subj}${threadExtent(s)}${threadAttachments(s)}${to}${cc}`;
  }
  if (src === "slack_file") {
    const sender = s.sender ? ` by ${s.sender}` : "";
    const posted = s.received_at ? `, posted ${fmtDateTime(s.received_at)}` : "";
    const where = s.thread_subject ? ` in ${s.thread_subject}` : "";
    return `Slack: file "${s.display_name}" shared${where}${sender}${posted}`;
  }
  if (src === "bma_sync") return "synced from the Production site";
  if (src === "calculated") return `generated by InRange${on}`;
  if (src === "manual_zip_member") {
    const arch = s.source_zip_filename ? ` "${s.source_zip_filename}"` : "";
    const drive = s.drive_path ? ` (Drive: "${s.drive_path}")` : "";
    return `uploaded (member of archive${arch})${by}${on}${drive}`;
  }
  if (src === "manual") {
    // Only assert "local file" when the canonical origin confirms it.
    const label = origin === "local" ? "uploaded (local file)" : "uploaded";
    return `${label}${by}${on}`;
  }
  return `${src}: ${s.display_name}`;
}

// Collapse sightings that describe the SAME arrival. One ingest routinely records
// two rows: the sync path writes a `gmail_attachment` sighting and the
// upload-completion door writes a `manual` one for the same message (CLAUDE.md
// principle 17 - "185 gmail attachments as `manual`"). They cannot dedupe in the
// BE: `_find_matching_sighting` scopes by `source` first, so they are different
// places by construction. And `describe` deliberately remaps `manual` onto the
// upload's canonical origin, so the two render as identical text - leaving a count
// that says "Seen in 2 places" when there is one place recorded twice.
//
// Deduping on the RENDERED line is the honest fix at this layer: if two sightings
// are indistinguishable to the operator, they are one place. Keeps the first of
// each group, which after the newest-first sort is the most recent.
export function dedupeRenderedSightings(sightings: Sighting[]): Sighting[] {
  // Prefer the row whose `source` is NOT the unreliable 'manual' catch-all, so the
  // survivor is meaningful rather than whichever happened to sort first. Mirrors the
  // BE dedupe_rendered_sightings, which needs the same rule because its caller
  // branches on the raw source.
  const best = new Map<string, Sighting>();
  for (const s of [...sightings].sort((a, b) => sortMs(b) - sortMs(a))) {
    const line = describe(s);
    const kept = best.get(line);
    if (!kept || (kept.source === "manual" && s.source !== "manual")) best.set(line, s);
  }
  return [...best.values()].sort((a, b) => sortMs(b) - sortMs(a));
}

interface Props {
  sightings: Sighting[];
  // Collapse a SINGLE sighting behind the counter too ("Seen in 1 place"), instead
  // of rendering that one line inline. Purely presentation density - the expanded
  // text is identical either way, so the surfaces still agree on WHAT they say.
  //
  // On by default only for the Files tab's Source column, where the operator scans
  // a table and a tidy column of counters beats a ragged mix of counters and full
  // provenance sentences. Off everywhere else: on the conflict modal / tracker the
  // sighting line IS the candidate's source line and is what the operator decides
  // on, so hiding it behind a click would cost a decision a click.
  collapseSingle?: boolean;
}

export function SightingList({ sightings, collapseSingle = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  if (sightings.length === 0) return null;
  // Newest displayed-time first, same order the agent sees (BE sorts desc on the
  // per-source meaningful time), so the collapsed summary expands into the same
  // reading order the model gets - and one entry per distinct place.
  // Already newest-first, and one row per distinct rendered line.
  const ordered = dedupeRenderedSightings(sightings);
  const collapsible = ordered.length > 1 || collapseSingle;
  // A <span> wrapper (display:block via CSS), not a <div>: several call sites
  // render this inside an inline <span>, and a block-level child there is invalid
  // nesting. Same shape the bare <ul> had before the collapse landed.
  return (
    <span className={styles.wrap}>
      {collapsible && (
        <button
          type="button"
          className={styles.summaryButton}
          aria-expanded={expanded}
          title={
            ordered.length === 1
              ? "Show where this file came from"
              : "This file was seen in more than one place - show where"
          }
          onClick={() => setExpanded((v) => !v)}
        >
          Seen in {ordered.length} place{ordered.length === 1 ? "" : "s"}{" "}
          <Chevron open={expanded} />
        </button>
      )}
      {(!collapsible || expanded) && (
        <ul className={styles.list}>
          {ordered.map((s, i) => (
            <li key={i} className={s.deprecated ? styles.deprecated : undefined}>
              {describe(s)}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

// The deprecated badge, shared so the Files tab row and the conflict candidate
// render it identically.
export function DeprecatedBadge() {
  return (
    <span className={styles.badge} title="Filed at a Deprecated Drive path - superseded">
      DEPRECATED
    </span>
  );
}
